import React, { useState } from 'react';
import { Typography, Checkbox, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

import {
  ContentContainer,
  FlexContainer,
  HeaderBox,
  Title,
  ConsentLabel,
} from './EmailAuthForm.styles';

import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { validateEmail } from '@/utils/stringValidators';

const schema = z.object({
  email: z.string().transform((v) => v.trim()).refine(
    (v) => validateEmail(v),
    { message: 'coverage.errors.email_error' },
  ),
  termsAccepted: z.boolean().refine((v) => v === true),
});

type AddEmailForm = z.infer<typeof schema>;

export interface EmailAuthFormProps {
  onSubmit: (email: string) => void | Promise<void>;
  loading?: boolean;
}

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({ onSubmit, loading = false }) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState('');

  const { handleSubmit, control, formState } = useForm<AddEmailForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      termsAccepted: false,
    },
  });

  const onFormSubmit = async (data: AddEmailForm) => {
    setSubmitError('');
    try {
      await onSubmit(data.email);
    } catch {
      setSubmitError(t('coverage.pre_onboarding.email_auth.error'));
    }
  };

  return (
    <ContentContainer>
      <Header />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.email_auth.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.email_auth.sub_title')}
          </Typography>
        </HeaderBox>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="email"
                label={t('coverage.pre_onboarding.email_auth.email_label')}
                inputProps={{
                  placeholder: t('coverage.pre_onboarding.email_auth.email_placeholder'),
                }}
                error={!!fieldState.error}
                helperText={fieldState.error ? t('coverage.errors.email_error') : undefined}
                required
              />
            )}
          />

          <Controller
            name="termsAccepted"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <ConsentLabel
                label={(
                  <Typography>
                    {t('coverage.pre_onboarding.email_auth.read_and_accept')}
                    {' '}
                    <Link
                      href={t('footer.privacy_notice.link')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('coverage.pre_onboarding.email_auth.privacy_policy')}
                    </Link>
                    {' '}
                    {t('coverage.pre_onboarding.email_auth.and')}
                    {' '}
                    <Link
                      href={t('footer.terms_of_use.link')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('coverage.pre_onboarding.email_auth.terms_of_use')}
                    </Link>
                    *
                  </Typography>
                )}
                control={(
                  <Checkbox
                    {...field}
                    checked={Boolean(field.value)}
                    data-testid="terms-checkbox"
                  />
                )}
              />
            )}
          />

          <Button
            variant="contained"
            data-testid="continue-button"
            type="submit"
            fullWidth
            color="primary"
            disabled={!formState.isValid || loading}
            rounded
            style={{ borderRadius: 100 }}
          >
            {loading
              ? t('coverage.pre_onboarding.email_auth.submitting')
              : t('coverage.pre_onboarding.email_auth.continue')}
          </Button>

          {submitError && (
            <Typography color="error" data-testid="submit-error" style={{ marginTop: 8 }}>
              {submitError}
            </Typography>
          )}

        </form>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export default EmailAuthForm;
